import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook to manage PDF preview state across pages.
 * Returns:
 *   - previewState: {url, title, filename} or null
 *   - openPreview(apiPath, options): downloads via axios (with Authorization header),
 *     creates a blob URL, and opens the modal. Works with ANY backend PDF endpoint
 *     (no need to support ?token= query).
 *   - closePreview(): cleans up blob URL and closes
 */
export const usePdfPreview = () => {
  const [previewState, setPreviewState] = useState(null);

  const openPreview = useCallback(async (apiPath, { title = 'Aperçu PDF', filename = 'document.pdf' } = {}) => {
    const tid = toast.loading('Génération du PDF...');
    try {
      // Also append ?token= so the request works on backends that don't yet
      // accept the Authorization: Bearer header (older devis/facture endpoints).
      const token = localStorage.getItem('token');
      const sep = apiPath.includes('?') ? '&' : '?';
      const urlWithToken = token ? `${API}${apiPath}${sep}token=${encodeURIComponent(token)}` : `${API}${apiPath}`;
      const res = await axios.get(urlWithToken, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      toast.dismiss(tid);
      setPreviewState({ url, title, filename });
    } catch (e) {
      toast.dismiss(tid);
      if (!navigator.onLine || e?.code === 'ERR_NETWORK' || e?.isOffline) toast.error('Hors ligne : la génération du PDF nécessite une connexion internet');
      else toast.error('Erreur lors de la génération du PDF');
    }
  }, []);

  const closePreview = useCallback(() => {
    if (previewState?.url) URL.revokeObjectURL(previewState.url);
    setPreviewState(null);
  }, [previewState]);

  return { previewState, openPreview, closePreview };
};
